/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  Account,
  Application,
  Asset,
  Device,
  EntityStatus,
  Group,
  Link,
  Role,
  User
} from '@ion/core/src/types/entities';
import {
  Account as APITierAccount,
  Application as APITierApplication,
  Asset as APITierAsset,
  Device as APITierDevice,
  Group as APITierGroup,
  Permission as APITierPermission,
  Role as APITierRole,
  User as APITierUser
} from './types';

/**
 * Provides a resolver for converting an API-Tier `User` to a Platform `User` entity.
 * Specific properties are converted to HATEAOS Links where required for backwards
 * compatibility, while also retaining the original references within the `enriched`
 * property. The fields within the `enriched` property are to then be used in place
 * of additional CTC service calls, and then nullified, while the CTC attribute links
 * can continue to be used so as to support backwards compatibility.
 *
 * @param   user The API Tier `User` which is to be converted to a Platform `User`.
 * @param   serviceURL an optional Service URL from which CTC entity Links will be
 *          created for.
 * @returns The given API Tier `User` entity converted to a Platform `User` entity.
 */
export const resolveUser = (user: APITierUser, url?: string): User => {
  const result: User = resolve<User>(user as unknown as User, userResolver);
  const userRoles = [];
  if (url) {
    const {account} = user;
    result.account = createLink({
      id       : account?.id,
      title    : account?.name,
      status   : account?.status,
      resource : 'accounts',
      rel      : 'account',
      url
    });
    result.roles = (user.roles as APITierRole[])?.map(
      (role: APITierRole) => {
        const _role: any = {...role};
        _role.application = createLink({
          id       : role.applicationKey,
          resource : 'applications',
          rel      : 'application',
          url
        });
        _role.self = createLink({
          id       : role.id,
          status   : role.status,
          resource : 'role',
          rel      : 'self',
          url
        });
        userRoles.push(_role);
        return createLink({
          id       : role.id,
          title    : role.name,
          status   : role.status,
          resource : 'roles',
          rel      : 'role',
          url
        });
      });
    result.applications = (user.applications as APITierApplication[])?.map(
      (app: APITierApplication) => (
        createLink({
          status   : resolveApplicationStatus(user.applications as Application[], app.applicationKey),
          id       : app.applicationKey,
          title    : app.name,
          resource : 'applications',
          rel      : 'application',
          url
        })));
  }
  return {
    ...result,
    enriched : {
      account : user?.account,
      roles   : userRoles
    }
  };
};

/**
 * Provides a utility which, given a list of applications and a specific `appKey`,
 * returns the `status` of the application which matches the `appKey`.
 *
 * @param   applications A list of applications for which the given `appKey` is
 *          to be resolved.
 * @param   appKey The application key for which the status is to be resolved.
 * @returns The EntityStatus of the application for the given `appKey`;
 */
const resolveApplicationStatus = (applications: Application[], appKey: string) => (
  applications.find(({applicationKey}) => applicationKey === appKey)?.status
);

/**
 * Provides a resolver for converting `APITierPermissions` to CTC Permission Links.
 *
 * ```typescript
 *  import {resolvePermissions} from '@ion/services/src/graphql/ResourceResolver';
 *  ...
 *
 *  // convert APITier Permissions to an array of CTC Permission Links ...
 *  const links = resolvePermissions(permissions);
 * ```
 *
 * @see {@link APITierPermission}
 * @see {@link Link}
 *
 * @param   permissions An array of `APITierPermissions` to be converted to CTC `Links`.
 * @param   url The `URL which is to be used when creating the link `href`.
 * @returns An array of CTC `Links` based on the provided `APITierPermissions`
 */
export const resolvePermissions = (permissions: APITierPermission[], url: string): Link[] => (
  permissions.map(({id, resource, action}: APITierPermission) => (
    createLink({
      title : `${resource}-${action}`,
      rel   : 'permission',
      url,
      id
    })
  ))
);

/**
 * Provides a resolver for converting an array of API-Tier `User` entities to an
 * array of Platform `User` entities.
 *
 * @param   users The API Tier `User` which is to be converted to a Platform `User`.
 * @returns The given API Tier `User` entity converted to a Platform `User` entity.
 */
export const resolveUsers = (users: APITierUser[], url?: string): User[] => (
  users.map(user => resolveUser(user, url))
);

/**
 * Provides a resolver for converting an array of API-Tier `Role` entities to an
 * array of Platform `Role` entities.
 *
 * @param   roles The API Tier `Role` which is to be converted to a Platform `Role`.
 * @returns The given API Tier `Role` entity converted to a Platform `Role` entity.
 */
export const resolveRoles = (roles: APITierRole[]): Role[] => (
  roles.map(role => resolve<Role>(role as unknown as Role, roleResolver))
);

/**
 * Provides a resolver for converting an array of API-Tier `Device` entities to an
 * array of Platform `Device` entities.
 *
 * @param   devices The API Tier `Device` which is to be converted to a Platform `Device`.
 * @returns The given API Tier `Device` entity converted to a Platform `Device` entity.
 */
export const resolveDevices = (devices: APITierDevice[]): Device[] => (
  devices.map(device => resolve<Device>(device as unknown as Device, deviceResolver))
);

/**
 * Provides a resolver for converting an array of API-Tier `Asset` entities to an
 * array of Platform `Asset` entities.
 *
 * @param   assets The API Tier `Asset` which is to be converted to a Platform `Asset`.
 * @returns The given API Tier `Asset` entity converted to a Platform `Asset` entity.
 */
export const resolveAssets = (assets: APITierAsset[]): Asset[] => (
  assets.map(asset => resolve<Asset>(asset as unknown as Asset, assetResolver))
);

/**
 * Provides a resolver for converting an array of API-Tier `Group` entities to an
 * array of Platform `Group` entities.
 *
 * @param   groups The API Tier `Group` which is to be converted to a Platform `Group`.
 * @returns The given API Tier `Group` entity converted to a Platform `Group` entity.
 */
export const resolveGroups = (groups: APITierGroup[]): Group[] => (
  groups.map(group => resolve<Group>(group as unknown as Group, groupResolver))
);

/**
 * Provides a resolver for converting an API-Tier `Account` to a Platform `Account`
 * entity.
 *
 * @param   account The API Tier `Account` which is to be converted to a Platform
 *          `Account`.
 * @returns The given API Tier `Account` entity converted to a Platform `Account`
 *          entity.
 */
export const resolveAccount = (account: APITierAccount): Account => (
  resolve<Account>(account as unknown as Account, accountResolver)
);

/**
 * Provides a resolver for converting an API-Tier `Application` to a Platform `Application`
 * entity.
 *
 * @param   application The API Tier `Application` which is to be converted to a Platform
 *          `Application`.
 * @returns The given API Tier `Application` entity converted to a Platform `Application`
 *          entity.
 */
export const resolveApplication = (application: APITierApplication): Application => (
  resolve<Application>(application as unknown as Application, applicationResolver)
);

/**
 * @private
 *
 * Provides a utility which manages converting an API Data Model to a CTC Data Model.
 * By design, platform Entity properties are defined in camelCase, while API Tier Entity
 * properties are defined in snake_case. The `resolve` utility allows for converting API
 * tier entities to platform entities in order to provide a consistent API.
 *
 * @param   entity An API-Tier Entity type which is to be converted to a Platform Entity
 *          Type.
 * @returns The provided `entity` converted from an API-Tier Entity type to a Platform
 *          Entity Type.
 */
const resolve = <T>(entity: Partial<T>, resolver = defaultResolver): T => {
  if (entity && typeof entity === 'object') {
    if (!Array.isArray(entity)) {
      const result: any = {};
      for (const attribute in entity) {
        const {property, value}: ResolverResult = resolver<Partial<T>>(attribute, entity);
        result[property] = resolve(value);
      }
      return result as T;
    }
    return (entity as any[]).map(e => resolve(e, resolver)) as unknown as T;
  }
  return entity as T;
};

/**
 * @private
 *
 * Defines the ResolverResult type which provides a transformed property and value
 * based on the provided entity and attribute.
 */
type ResolverResult = {
  property: string
  value: any
};

/**
 * Provides the default resolver which simply converts property names from snake
 * case to camel case. For use-cases where more complex property and / or value
 * transformations are required, entity specific resolvers should be implemented.
 *
 * @param   attribute The property which is to be transformed.
 * @param   entity The entity instance for which the property and value are to be
 *          transformed.
 * @returns The given `attribute` and entity transformed to a normalized `property`
 *          and `value`.
 */
const defaultResolver = <T>(attribute: string, entity: Partial<T>): ResolverResult => ({
  property : attribute?.replace(/_(\w)/g, (_, char) => char?.toUpperCase()),
  value    : entity[attribute]
});

/**
 * Defines the default entity attribute resolver which will return the mapped
 * attribute (if defined), otherwise fallsback to using the `defaultResolver`.
 */
const entityResolver = <T>(map: any, attribute: string, entity: Partial<T>): ResolverResult => {
  const property = map[attribute];
  if (property) {
    return {
      value : entity[attribute],
      property
    };
  }
  return defaultResolver<APITierUser>(attribute, entity);
};

/**
 * Defines the Link Options which are used to define the synthesized link to be
 * added to the given entity instance.
 */
type LinkOptions = {
  id: number | string
  url: string
  title?: string
  rel?: string
  resource?: string
  status?: string
};

/**
 * The `createLink` function provides a utility which synthesizes the creation of
 * a CTC Link attribute on a given Entity.
 *
 * This can be used for backwards compatibility whereby client code has dependencies
 * on existing CTC types which require a Link attribute, and the corresponding API
 * Tier Entity does not natively provide these values as links.
 */
export const createLink = ({id, url, title, rel, resource, status}: LinkOptions): Link => {
  const link: Link = {
    href  : `${url}/${resource}/${id}`,
    title : title || '',
    rel   : rel || ''
  };
  if (status) {
    link.status = status as unknown as EntityStatus;
  }
  return link;
};

/**
 * Provides a mapping of API Tier `User` attributes which require specific
 * conversions to CTC `User` attributes.
 */
const userAttributeMap = {
};

/**
 * Provides an `User` specific resolver for resolving API Tier `User` attributes
 * and values to their corresponding CTC equivalents.
 */
const userResolver = (attribute: string, entity: APITierUser): ResolverResult => {
  return entityResolver<APITierUser>(userAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Role` attributes which require specific
 * conversions to CTC `Role` attributes.
 */
const roleAttributeMap = {
};

/**
 * Provides an `Role` specific resolver for resolving API Tier `Role` attributes
 * and values to their corresponding CTC equivalents.
 */
const roleResolver = (attribute: string, entity: APITierRole): ResolverResult => {
  return entityResolver<APITierRole>(roleAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Device` attributes which require specific
 * conversions to CTC `Device` attributes.
 */
const deviceAttributeMap = {
};

/**
 * Provides an `Device` specific resolver for resolving API Tier `Device` attributes
 * and values to their corresponding CTC equivalents.
 */
const deviceResolver = (attribute: string, entity: APITierDevice): ResolverResult => {
  return entityResolver<APITierDevice>(deviceAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Group` attributes which require specific
 * conversions to CTC `Group` attributes.
 */
const groupAttributeMap = {
};

/**
 * Provides an `Group` specific resolver for resolving API Tier `Group` attributes
 * and values to their corresponding CTC equivalents.
 */
const groupResolver = (attribute: string, entity: APITierGroup): ResolverResult => {
  return entityResolver<APITierGroup>(groupAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Account` attributes which require specific
 * conversions to CTC `Account` attributes.
 */
const accountAttributeMap = {
  accountStatus : 'status'
};

/**
 * Provides an `Account` specific resolver for resolving API Tier `Account`
 * attributes and values to their corresponding CTC equivalents.
 */
const accountResolver = (attribute: string, entity: APITierAccount): ResolverResult => {
  return entityResolver<APITierAccount>(accountAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Application` attributes which require specific
 * conversions to CTC `Application` attributes.
 */
const applicationAttributeMap = {
};

/**
 * Provides an `Application` specific resolver for resolving API Tier `Application`
 * attributes and values to their corresponding CTC equivalents.
 */
const applicationResolver = (attribute: string, entity: APITierApplication): ResolverResult => {
  return entityResolver<APITierApplication>(applicationAttributeMap, attribute, entity);
};

/**
 * Provides a mapping of API Tier `Asset` attributes which require specific
 * conversions to CTC `Asset` attributes.
 */
const assetAttributeMap = {
  assetStatus : 'status'
};

/**
 * Provides an `Asset` specific resolver for resolving API Tier `Asset`
 * attributes and values to their corresponding CTC equivalents.
 */
const assetResolver = (attribute: string, entity: APITierAsset): ResolverResult => {
  return entityResolver<APITierAsset>(assetAttributeMap, attribute, entity);
};
